import { useCallback, useEffect, useMemo, useState } from 'react';
import { AppDataGrid, type AppColumn } from '../../../components/AppDataGrid';
import { AppToolbar } from '../../../components/AppToolbar';
import { FormTitle } from '../../../components/FormTitle';
import { useNotify } from '../../../components/notifications';
import { DAILY_QC_FORM, errorText, fetchLogbooks, type DailyQcLogbook, type DailyQcTodayState } from './dailyQcApi';

/** Today badge — the shared pill colours: Done green · Due blue · Overdue red. */
const TODAY_PILL: Record<DailyQcTodayState, [string, string] | null> = {
  done: ['ok', 'Done'], due: ['info', 'Due'], overdue: ['alert', 'Overdue'], none: null,
};

/**
 * Level 1 — the ACTIVE logbooks (retired ones are hidden), with each one's schedule,
 * last entry, next due date and today's state. Clicking a logbook opens its history.
 */
export default function DailyQcLogbookList({ onHome, onOpen }: { onHome?: () => void; onOpen: (templateId: number) => void }) {
  const notify = useNotify();
  const [rows, setRows] = useState<DailyQcLogbook[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadMs, setLoadMs] = useState<number | null>(null);
  const [search, setSearch] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    const t0 = performance.now();
    try {
      setRows(await fetchLogbooks());
    } catch (e) {
      notify.error(errorText(e, 'Failed to load the Daily QC logbooks.'));
    } finally {
      setLoadMs(performance.now() - t0);
      setLoading(false);
    }
  }, [notify]);

  useEffect(() => { void load(); }, [load]);

  const shown = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter((b) => [b.name, b.category, b.instrumentCode, b.instrumentName, String(b.templateId)]
      .some((v) => (v ?? '').toLowerCase().includes(q)));
  }, [rows, search]);

  const toDo = rows.filter((b) => b.todayState === 'due' || b.todayState === 'overdue').length;
  const overdue = rows.filter((b) => b.todayState === 'overdue').length;

  const cols: AppColumn<DailyQcLogbook>[] = [
    { field: 'name', header: 'Logbook', width: 240, text: (r) => r.name,
      render: (r) => <span className="bl-link dqc-book-link">{r.name}</span> },
    { field: 'category', header: 'Category', width: 150, text: (r) => r.category ?? '' },
    { field: 'instrumentCode', header: 'Instrument', width: 200,
      text: (r) => [r.instrumentCode, r.instrumentName].filter(Boolean).join(' '),
      render: (r) => (<>{r.instrumentCode} <span className="dqc-muted">{r.instrumentName}</span></>) },
    { field: 'schedule', header: 'Schedule', width: 120, text: (r) => r.schedule },
    { field: 'lastEntry', header: 'Last Entry', width: 110, type: 'date' },
    { field: 'nextDue', header: 'Next Due', width: 110, type: 'date' },
    { field: 'todayState', header: 'Today', width: 90, align: 'center',
      text: (r) => TODAY_PILL[r.todayState]?.[1] ?? '',
      render: (r) => {
        const pill = TODAY_PILL[r.todayState];
        return pill ? <span className={`adg-pill ${pill[0]}`}>{pill[1]}</span> : null;
      } },
    { field: 'templateId', header: 'Logbook ID', width: 90, type: 'number', text: (r) => String(r.templateId) },
  ];

  return (
    <div className="bl-page">
      <div className="bl-top">
        <div className="dp-breadcrumb">
          <span className="dp-bc-link" onClick={onHome}>Home</span>
          <i className="ti ti-chevron-right" />
          <span>Analysis</span>
          <i className="ti ti-chevron-right" />
          <span className="dp-bc-active">Daily QC</span>
        </div>
        <FormTitle pageId="dailyqc" auditFormName={DAILY_QC_FORM} loadMs={loadMs} loading={loading} />
      </div>

      <div className="bl-grid-area">
        {/* Owner request 2026-09-29: no Export button; the Search box + summary sit on the
            Refresh row (AppToolbar `right` slot, which follows Refresh when there is no Export). */}
        <div style={{ margin: '0 0 8px' }}>
          <AppToolbar onRefresh={load} refreshing={loading}
            right={(
              <div className="dqc-toolbar-search">
                <div className="list-search">
                  <span className="tb-i">🔍</span>
                  <input type="text" placeholder="Search logbooks…" value={search}
                    onChange={(e) => setSearch(e.target.value)} data-unsaved-ignore />
                  {search && <span className="list-search-clear" onClick={() => setSearch('')}>×</span>}
                </div>
                <span className="dqc-summary">
                  {rows.length} active logbook{rows.length === 1 ? '' : 's'}, {toDo} to complete today{overdue ? `, ${overdue} overdue` : ''}
                </span>
              </div>
            )} />
        </div>

        <div className="bl-card bl-card-flex">
          <AppDataGrid<DailyQcLogbook>
            headerLeft={(
              <div className="dqc-sectitle dqc-sectitle--list">
                Active Logbooks <span className="dqc-sectnote">Open a logbook to see its history and add entries.</span>
              </div>
            )}
            rows={shown} columns={cols} getRowId={(r) => r.templateId}
            onRowClick={(r) => onOpen(r.templateId)}
            loading={loading}
            emptyText={search ? 'No logbooks match your search.' : 'No active logbooks.'}
            footerNote="Retired logbooks are hidden."
          />
        </div>
      </div>
    </div>
  );
}
